import { getNativePlatform, isNativeApp } from '../utils/platform'
import { TimeoutError, withTimeout } from '../utils/with-timeout'
import {
  clearBiometricUnlockClock,
  clearWebMfaClock,
  recordBiometricUnlock,
} from '../stores/app-lock-store'
import { SOFT_LOCK_VERIFY_COPY } from '../features/security/session-security-policy'
import {
  describeBiometricError,
  describeBiometricHang,
  extractBiometricErrorCode,
  isBiometricCancelCode,
  type BiometricVerifyResult,
} from '../features/security/biometric-errors'
import {
  BIOMETRIC_AVAILABILITY_PROBE_MS,
  BIOMETRIC_VERIFY_PROBE_MS,
  settleBiometricAvailabilityProbe,
  settleBiometricVerify,
  type BiometricAvailability,
} from '../features/security/biometric-availability-probe'

export { BIOMETRIC_AVAILABILITY_PROBE_MS, BIOMETRIC_VERIFY_PROBE_MS }
export type { BiometricAvailability }

/** iOS: brief pause after becoming visible so LAContext can present Face ID. */
const IOS_PROMPT_READY_MS = 300

/**
 * Thin service-layer wrapper around @capgo/capacitor-native-biometric.
 *
 * Biometric checks here NEVER replace or extend the Cognito session — they are
 * a purely local, additive gate:
 *  - Soft-lock: L3+ re-verify every 12h (`lastBiometricUnlockAt`); optional
 *    opt-in for lower tiers.
 *  - `confirmStepUp` re-proves device ownership before sensitive account actions.
 *
 * We only ever persist a non-sensitive "enabled" flag plus an opaque marker in
 * the plugin's Keystore/Keychain-backed secure storage — never a credential we
 * read back for real authentication.
 *
 * Fallback chain (30C): biometric → device passcode (`useFallback: true`) →
 * Cognito password (caller signs out / "Use password instead").
 */

const SECURE_SERVER = 'com.ascendhealth.comms.biometric'
const ENABLED_KEY = 'biometric-unlock-enabled'

export type StepUpResult = { ok: true; skipped?: boolean } | { ok: false; message: string }

export type { BiometricVerifyResult }

/**
 * NEVER return the `Preferences` plugin proxy from an async function (or
 * resolve a promise with it). Capacitor's `registerPlugin` proxy fabricates a
 * method stub for EVERY property — including `then` — so the promise
 * resolution procedure treats the proxy as a thenable, calls
 * `Preferences.then()`, and the whole chain rejects with
 * `"Preferences.then()" is not implemented on android/ios`. On real devices
 * that made `isBiometricUnlockEnabled()` permanently false and the enroll
 * flag unpersistable — every cold start re-locked and re-prompted regardless
 * of the 12h clock (builds ≤93/59). Returning the module namespace (not the
 * proxy) keeps the resolution value non-thenable.
 */
async function preferences() {
  const mod = await import('@capacitor/preferences')
  return {
    get: (options: { key: string }) => mod.Preferences.get(options),
    set: (options: { key: string; value: string }) => mod.Preferences.set(options),
    remove: (options: { key: string }) => mod.Preferences.remove(options),
  }
}

/**
 * iOS `evaluatePolicy` during the foreground transition can neither present
 * Face ID nor invoke its reply (`notInteractive` hang). Wait until the
 * WebView is visible, then a short beat for the window to become key.
 * Android AuthActivity doesn't need this; the hang budget still covers it.
 */
async function waitForNativePromptReady(): Promise<void> {
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
    await new Promise<void>((resolve) => {
      const done = () => {
        clearTimeout(timer)
        document.removeEventListener('visibilitychange', onVis)
        resolve()
      }
      const onVis = () => {
        if (document.visibilityState === 'visible') done()
      }
      const timer = setTimeout(done, 1_000)
      document.addEventListener('visibilitychange', onVis)
    })
  }
  if (getNativePlatform() === 'ios') {
    await new Promise<void>((resolve) => {
      setTimeout(resolve, IOS_PROMPT_READY_MS)
    })
  }
}

/**
 * Whether the device has a usable local-auth method to prompt against.
 *
 * `allowPasscodeFallback` widens the check to "biometrics OR a device
 * passcode/PIN is configured" — used for soft-lock + step-up, where a
 * passcode is an acceptable fallback so we never hard-block a legitimate
 * session just because Face ID/Touch ID isn't enrolled.
 */
export async function getBiometricAvailability(options?: {
  allowPasscodeFallback?: boolean
  /** Override the hang budget (tests). */
  timeoutMs?: number
}): Promise<BiometricAvailability> {
  if (!isNativeApp()) return { available: false, label: 'Biometrics' }
  const timeoutMs = options?.timeoutMs ?? BIOMETRIC_AVAILABILITY_PROBE_MS
  return settleBiometricAvailabilityProbe(async () => {
    const { NativeBiometric } = await import('@capgo/capacitor-native-biometric')
    return NativeBiometric.isAvailable({
      useFallback: options?.allowPasscodeFallback ?? false,
    })
  }, timeoutMs)
}

/** Has the user opted into (or been enrolled for) biometric soft-lock on this device? */
export async function isBiometricUnlockEnabled(options?: { timeoutMs?: number }): Promise<boolean> {
  if (!isNativeApp()) return false
  const timeoutMs = options?.timeoutMs ?? BIOMETRIC_AVAILABILITY_PROBE_MS
  try {
    return await withTimeout(
      (async () => {
        const prefs = await preferences()
        const { value } = await prefs.get({ key: ENABLED_KEY })
        return value === '1'
      })(),
      timeoutMs,
      'Timed out reading biometric unlock preference',
    )
  } catch {
    return false
  }
}

/**
 * Single-flight guard around the native `verifyIdentity()` call.
 *
 * Both platforms break when a second prompt is issued while one is still
 * pending: Android queues another AuthActivity — the OS prompt "comes back"
 * right after a successful verify, and stacked saved plugin calls can clobber
 * each other so the success never reaches JS (the build-91 endless re-verify
 * loop) — and iOS stacks LAContext.evaluatePolicy calls until Face ID stops
 * presenting (the build-57 freeze). Reusing the pending call means every
 * caller — sheet auto-prompt, Try again, ensureSoftUnlocked deep links,
 * step-up — awaits the SAME OS prompt instead of stacking a new one.
 *
 * A truly hung bridge call (never settles) therefore also absorbs retries;
 * that is deliberate — the escape hatch for that state is the always-tappable
 * "Use password instead" on the sheet, not a second native prompt.
 */
let inFlightNativeVerify: Promise<void> | null = null

/** Test-only: forget a pending native verify between test cases. */
export function __resetInFlightNativeVerifyForTests(): void {
  inFlightNativeVerify = null
}

function startNativeVerify(reason: string): Promise<void> {
  if (inFlightNativeVerify) return inFlightNativeVerify
  const promise = (async () => {
    await waitForNativePromptReady()
    const { NativeBiometric } = await import('@capgo/capacitor-native-biometric')
    await NativeBiometric.verifyIdentity({
      reason,
      title: SOFT_LOCK_VERIFY_COPY,
      subtitle: reason,
      maxAttempts: 3,
      useFallback: true,
    })
  })()
  inFlightNativeVerify = promise
  promise.then(
    () => {
      if (inFlightNativeVerify === promise) inFlightNativeVerify = null
    },
    () => {
      if (inFlightNativeVerify === promise) inFlightNativeVerify = null
    },
  )
  return promise
}

/**
 * Prompt the biometric sensor (with OS-level passcode fallback).
 * On success, resets the 12h soft-lock clock (7A) unless `recordUnlock: false`.
 *
 * The success completion (`recordBiometricUnlock` + `onVerified`) is attached
 * to the native promise itself, so a success that lands AFTER the hang budget
 * (slow Face ID retry, passcode typing takes well over 4s) counts exactly the
 * same as a fast one. A timed-out attempt returns `timedOut: true` — the OS
 * prompt is usually still up and the attempt is still live, it just outlived
 * the UI budget.
 *
 * Failures carry the plugin error code + user-facing copy so callers can
 * distinguish "cancelled" from "hardware can't verify" — previously every
 * failure collapsed into a generic "didn't pass", which read as broken
 * biometrics on devices where the real cause was e.g. weak-only face unlock
 * (Android STRONG prompt mismatch) or a temporary lockout.
 */
export async function verifyBiometric(
  reason: string = SOFT_LOCK_VERIFY_COPY,
  options?: {
    recordUnlock?: boolean
    label?: string
    timeoutMs?: number
    /** Runs exactly once per attempt on native success — even a late one. */
    onVerified?: () => void | Promise<void>
  },
): Promise<BiometricVerifyResult> {
  const label = options?.label ?? 'Biometrics'
  if (!isNativeApp()) {
    return { ok: false, cancelled: false, message: 'Biometrics are only available in the app.' }
  }
  const timeoutMs = options?.timeoutMs ?? BIOMETRIC_VERIFY_PROBE_MS
  const verifyPromise = startNativeVerify(reason)
  // Authoritative completion: never lose a genuine success. Losing one
  // re-locks a user who just verified — the build-91 unlock loop.
  const completed = verifyPromise.then(
    async () => {
      if (options?.recordUnlock !== false) {
        recordBiometricUnlock()
      }
      await options?.onVerified?.()
    },
    () => {},
  )
  try {
    await settleBiometricVerify(() => verifyPromise, timeoutMs)
    await completed
    return { ok: true }
  } catch (err) {
    if (err instanceof TimeoutError) {
      // OS sheet is likely still up (slow Face ID / passcode). The attempt
      // stays live via `completed`; the sheet leaves Waiting so password
      // fallback is reachable, but a late success still unlocks.
      return { ok: false, cancelled: false, timedOut: true, message: describeBiometricHang(label) }
    }
    const code = extractBiometricErrorCode(err)
    return {
      ok: false,
      code,
      cancelled: isBiometricCancelCode(code),
      message: describeBiometricError(code, label),
    }
  }
}

export type EnableBiometricResult = { ok: true } | { ok: false; cancelled: boolean; message: string }

/**
 * Turn on biometric soft-lock. Requires a successful biometric check first,
 * then stores the opt-in flag + an opaque marker in secure storage and starts
 * the 12h unlock clock (25A).
 *
 * The enroll persistence runs off the native success itself (`onVerified`),
 * so a first-time enroll that outlives the hang budget — very common, the
 * user is reading a brand-new prompt — still enrolls and unlocks instead of
 * being discarded into an endless "Try again" loop.
 */
export async function enableBiometricUnlock(
  identifier: string,
  options?: { label?: string; timeoutMs?: number },
): Promise<EnableBiometricResult> {
  const label = options?.label ?? 'Biometrics'
  if (!isNativeApp()) {
    return { ok: false, cancelled: false, message: 'Biometric unlock is only available in the app.' }
  }
  let enrollSaveFailed = false
  const persistEnroll = async () => {
    // The user just proved presence — unlock first, persist flags after, so a
    // failed preference write can never strand a verified user behind the sheet.
    recordBiometricUnlock()
    try {
      const { NativeBiometric } = await import('@capgo/capacitor-native-biometric')
      // Stored only so the OS keystore has a record tied to the biometric set.
      // Not a credential we ever read back for auth — the Cognito session does that.
      await NativeBiometric.setCredentials({
        server: SECURE_SERVER,
        username: identifier,
        password: 'biometric-unlock',
      }).catch(() => {
        /* keystore write best-effort */
      })
      const prefs = await preferences()
      await prefs.set({ key: ENABLED_KEY, value: '1' })
    } catch {
      enrollSaveFailed = true
    }
  }
  const verify = await verifyBiometric('Enable biometric unlock', {
    recordUnlock: false,
    label,
    timeoutMs: options?.timeoutMs,
    onVerified: persistEnroll,
  })
  if (!verify.ok) {
    return { ok: false, cancelled: verify.cancelled, message: verify.message }
  }
  if (enrollSaveFailed) {
    return {
      ok: false,
      cancelled: false,
      message: `Couldn\u2019t save the ${label} setting on this device. Please try again.`,
    }
  }
  return { ok: true }
}

/** Turn off biometric soft-lock and remove the secure marker. */
export async function disableBiometricUnlock(): Promise<void> {
  if (!isNativeApp()) return
  try {
    const { NativeBiometric } = await import('@capgo/capacitor-native-biometric')
    await NativeBiometric.deleteCredentials({ server: SECURE_SERVER }).catch(() => {})
  } catch {
    /* plugin unavailable */
  }
  try {
    const prefs = await preferences()
    await prefs.remove({ key: ENABLED_KEY })
  } catch {
    /* non-fatal */
  }
  clearBiometricUnlockClock()
}

/**
 * Clear per-device soft-lock clocks (20A). Call on sign-out and password change
 * so the next session requires a fresh biometric / web MFA unlock. Does not
 * disable the biometric enroll flag — users stay enrolled after signing back in.
 * Web MFA clock is separate from the native 12h biometric clock.
 */
export function clearSessionSecurityClocks(): void {
  clearBiometricUnlockClock()
  clearWebMfaClock()
}

/**
 * Step-up confirmation gate for sensitive account actions (password/email
 * change, display name change, account deletion). Independent of soft-lock
 * enroll — always attempts biometric (or device-passcode-fallback) on native.
 *
 * On web, or on a device with no biometrics AND no passcode configured at all,
 * there's no local auth mechanism to check against, so we let the action
 * through (`skipped: true`) rather than permanently blocking legitimate
 * account management.
 *
 * A successful step-up also resets the 12h soft-lock clock — the user just
 * proved device ownership with the exact same chain (biometric → passcode),
 * so re-prompting the soft-lock moments later would be pure friction.
 */
export async function confirmStepUp(reason: string): Promise<StepUpResult> {
  if (!isNativeApp()) return { ok: true, skipped: true }
  const availability = await getBiometricAvailability({ allowPasscodeFallback: true })
  if (!availability.available) return { ok: true, skipped: true }
  const verify = await verifyBiometric(reason, { label: availability.label })
  if (verify.ok) return { ok: true }
  return { ok: false, message: verify.message }
}
